import { useEffect, useState, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import useChatStore from '@/stores/useChatStore'
import { ChatHistory } from '@/components/chat/ChatHistory'
import { MessageInput } from '@/components/chat/MessageInput'
import { useToast } from '@/components/ui/use-toast'
import { ApiErrorDisplay } from '@/components/chat/ApiErrorDisplay'
import { EmptyChat } from '@/components/chat/EmptyChat'
import useOrganizationStore from '@/stores/useOrganizationStore'
import { ChatHeader } from './ChatHeader'
import { SessionRecoveryIndicator } from './SessionRecoveryIndicator'

export const ChatInterface = () => {
  const {
    messages,
    isLoading,
    error,
    sendMessage,
    organizationId,
    setOrganizationId,
    sessionId,
    recoveryIndicator,
    setRecoveryIndicator,
  } = useChatStore()
  const {
    organizations,
    fetchOrganizations,
    isLoading: isLoadingOrgs,
  } = useOrganizationStore()
  const { toast } = useToast()
  const navigate = useNavigate()
  const location = useLocation()
  const [showRecoveryIndicator, setShowRecoveryIndicator] = useState(false)
  const recoveryTimerRef = useRef<number | null>(null)

  useEffect(() => {
    if (recoveryIndicator) {
      setShowRecoveryIndicator(true)
      recoveryTimerRef.current = window.setTimeout(() => {
        setShowRecoveryIndicator(false)
        setRecoveryIndicator(null)
      }, 5000)
    }
    return () => {
      if (recoveryTimerRef.current) {
        clearTimeout(recoveryTimerRef.current)
      }
    }
  }, [recoveryIndicator, setRecoveryIndicator])

  useEffect(() => {
    if (messages.length === 0 && !isLoading) {
      fetchOrganizations()
    }
  }, [messages.length, isLoading, fetchOrganizations])

  useEffect(() => {
    if (!organizationId && organizations.length > 0) {
      const defaultOrg = organizations.find((org) => org.isDefault)
      if (defaultOrg) {
        setOrganizationId(defaultOrg.id)
      } else if (organizations.length > 0) {
        setOrganizationId(organizations[0].id)
      }
    }
  }, [organizations, organizationId, setOrganizationId])

  const handleSendMessage = async (content: string) => {
    if (showRecoveryIndicator) {
      setShowRecoveryIndicator(false)
      if (recoveryTimerRef.current) {
        clearTimeout(recoveryTimerRef.current)
      }
      setRecoveryIndicator(null)
    }

    if (!organizationId) {
      toast({
        variant: 'destructive',
        title: 'Organization Not Selected',
        description: 'Please select an organization to start a chat.',
      })
      return
    }

    const isNewSessionOnIndex =
      location.pathname === '/' && messages.length === 0

    await sendMessage(content)

    if (isNewSessionOnIndex) {
      const finalSessionId = useChatStore.getState().sessionId
      if (finalSessionId) {
        navigate(`/session/${finalSessionId}`, { replace: true })
      }
    }
  }

  const header =
    organizationId && sessionId ? (
      <ChatHeader organizationId={organizationId} sessionId={sessionId} />
    ) : null

  const chatContent =
    messages.length === 0 && !isLoading ? (
      <EmptyChat
        onPromptClick={handleSendMessage}
        organizations={organizations}
        selectedOrgId={organizationId || undefined}
        setSelectedOrgId={setOrganizationId}
        isLoadingOrgs={isLoadingOrgs}
      />
    ) : (
      <ChatHistory
        messages={messages}
        isLoading={isLoading}
        onSuggestionClick={handleSendMessage}
      />
    )

  return (
    <div className="flex flex-col flex-1">
      {showRecoveryIndicator && <SessionRecoveryIndicator />}
      {header}
      {chatContent}
      {error && <ApiErrorDisplay message={error} />}
      <MessageInput
        onSendMessage={handleSendMessage}
        isLoading={isLoading}
        disabled={!organizationId}
      />
    </div>
  )
}
